import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, HelpCircle, ShieldAlert, History } from "lucide-react";

const FIELD_LABEL = {
  address: "Address",
  property_type: "Property Type",
  bedrooms: "Bedrooms",
  bathrooms: "Bathrooms",
  sqft: "Square Footage",
  acquisition_price: "Acquisition Price",
  jurisdiction: "Jurisdiction (City / County / ETJ)",
  zoning: "Zoning",
  intended_use: "Intended Use Assumption",
};

const EV_LEVELS = [
  { k: "verified_fact", l: "Verified Fact" },
  { k: "partner_confirmed", l: "Partner Confirmed" },
  { k: "public_source", l: "Public Source" },
  { k: "estimate", l: "Estimate" },
  { k: "assumption", l: "Assumption" },
  { k: "needs_verification", l: "Needs Verification" },
];

const isVerified = (ev) => ev === "verified_fact" || ev === "partner_confirmed";

export default function CriticalFactsEditor({ propertyId, onChanged }) {
  const [status, setStatus] = useState(null);
  const [prop, setProp] = useState(null);
  const [drafts, setDrafts] = useState({}); // {field: {value, evidence_level, source}}
  const [busy, setBusy] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const load = async () => {
    try {
      const [s, p] = await Promise.all([
        api.get(`/properties/${propertyId}/verification-status`),
        api.get(`/properties/${propertyId}`),
      ]);
      setStatus(s.data);
      setProp(p.data);
      setLoadError(false);
    } catch (e) {
      setLoadError(true);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [propertyId]);

  const save = async (field, resolveConflicts = false) => {
    const d = drafts[field];
    if (!d) return;
    setBusy(true);
    try {
      const { data } = await api.put(`/properties/${propertyId}/critical-facts`, {
        field,
        value: coerceValue(field, d.value),
        evidence_level: d.evidence_level,
        source: d.source || "",
        resolve_conflicts: resolveConflicts,
      });
      setStatus(data);
      setDrafts({ ...drafts, [field]: undefined });
      toast.success(`${FIELD_LABEL[field]} updated · Downstream packets marked stale`);
      onChanged?.();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const resolveConflict = async (field, chosenValue, evLevel, source) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/properties/${propertyId}/critical-facts/resolve-conflict`, {
        field, chosen_value: chosenValue, evidence_level: evLevel, source: source || "Manual resolution",
      });
      setStatus(data);
      toast.success(`${FIELD_LABEL[field]} conflict resolved`);
      onChanged?.();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Resolve failed");
    } finally {
      setBusy(false);
    }
  };

  if (!status) return (
    <Card className="p-6 border-slate-200 bg-white" data-testid="critical-facts-editor">
      {loadError ? (
        <div className="flex items-center justify-between gap-3">
          <div className="text-sm text-rose-700">Couldn't load critical facts.</div>
          <Button size="sm" variant="outline" onClick={load} data-testid="cf-retry-btn">Retry</Button>
        </div>
      ) : (
        <div className="text-sm text-slate-500">Loading critical facts…</div>
      )}
    </Card>
  );

  const done = status.verified_count;
  const total = status.total;
  const gateOk = status.ready_to_share;

  return (
    <Card className="p-6 border-slate-200 bg-white" data-testid="critical-facts-editor">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1">
            <ShieldAlert className="w-3.5 h-3.5" /> Critical Facts — verification checkpoint
          </div>
          <h3 className="text-lg font-semibold mt-0.5">
            {done}/{total} verified {status.conflict_count > 0 && <span className="ml-2 text-rose-600 text-sm">· {status.conflict_count} conflict{status.conflict_count>1?"s":""}</span>}
          </h3>
        </div>
        <div className={`px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-widest ${gateOk ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}`} data-testid="gate-status">
          {gateOk ? "Ready-to-Share gate: OPEN" : "Ready-to-Share gate: BLOCKED"}
        </div>
      </div>

      {!gateOk && (
        <div className="mb-4 p-3 rounded-md bg-rose-50 border border-rose-200 text-xs text-rose-800">
          A packet cannot be marked <strong>Ready to Share</strong> until every critical fact is at
          Verified Fact or Partner Confirmed with a source, and all conflicts are resolved.
        </div>
      )}

      <div className="space-y-2">
        {status.fields.map(f => {
          const d = drafts[f.field] || { value: f.value ?? "", evidence_level: f.evidence_level, source: f.source };
          const hasConflict = (f.conflicts || []).length > 0;
          const stateColor = hasConflict ? "border-rose-300 bg-rose-50/40" :
                             f.verified ? "border-emerald-200 bg-emerald-50/30" :
                             !f.has_value ? "border-slate-300 bg-slate-50/40" :
                             "border-amber-200 bg-amber-50/30";
          return (
            <div key={f.field} className={`p-3 rounded-md border ${stateColor}`} data-testid={`fact-${f.field}`}>
              <div className="flex items-center justify-between gap-3 mb-2">
                <div className="flex items-center gap-2">
                  {hasConflict ? <AlertTriangle className="w-4 h-4 text-rose-600" /> :
                   f.verified ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> :
                   <HelpCircle className="w-4 h-4 text-amber-600" />}
                  <div className="text-sm font-semibold text-slate-900">{FIELD_LABEL[f.field]}</div>
                  {hasConflict && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-rose-600 text-white font-semibold uppercase tracking-wider" data-testid={`conflict-${f.field}`}>Conflict</span>}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
                  {f.verified_at ? `Verified ${(f.verified_at || "").slice(0,10)}` : "Unverified"}
                </div>
              </div>

              <div className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-4">
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">Value</Label>
                  <Input
                    value={d.value ?? ""}
                    onChange={e => setDrafts({ ...drafts, [f.field]: { ...d, value: e.target.value } })}
                    className="text-xs" data-testid={`fact-value-${f.field}`}
                  />
                </div>
                <div className="col-span-3">
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">Evidence level</Label>
                  <select
                    value={d.evidence_level}
                    onChange={e => setDrafts({ ...drafts, [f.field]: { ...d, evidence_level: e.target.value } })}
                    className="w-full border border-slate-200 rounded-md px-2 py-1.5 text-xs bg-white"
                    data-testid={`fact-ev-${f.field}`}
                  >
                    {EV_LEVELS.map(o => <option key={o.k} value={o.k}>{o.l}</option>)}
                  </select>
                </div>
                <div className="col-span-4">
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">Source</Label>
                  <Input
                    value={d.source ?? ""}
                    onChange={e => setDrafts({ ...drafts, [f.field]: { ...d, source: e.target.value } })}
                    placeholder="e.g., Bexar CAD, owner walkthrough, MLS #…"
                    className="text-xs" data-testid={`fact-source-${f.field}`}
                  />
                </div>
                <div className="col-span-1">
                  <Button size="sm" onClick={() => save(f.field)} disabled={busy} className="bg-slate-900 hover:bg-slate-800 w-full text-xs" data-testid={`fact-save-${f.field}`}>
                    Save
                  </Button>
                </div>
              </div>

              {hasConflict && (
                <div className="mt-3 pt-3 border-t border-rose-200">
                  <div className="text-[10px] uppercase tracking-widest text-rose-700 font-semibold mb-2">Conflicting values — pick one to resolve</div>
                  <div className="grid gap-1.5">
                    <ConflictRow label="Current stored value" value={f.value} source={f.source} onPick={() => resolveConflict(f.field, f.value, "partner_confirmed", f.source)} testid={`resolve-current-${f.field}`} />
                    {(f.conflicts || []).map((c, i) => (
                      <ConflictRow key={i} label={`Alternate #${i+1}`} value={c.value} source={c.source} onPick={() => resolveConflict(f.field, c.value, "partner_confirmed", c.source)} testid={`resolve-alt-${f.field}-${i}`} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {(prop?.verification_history || []).length > 0 && (
        <div className="mt-4">
          <button className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1" onClick={() => setShowHistory(!showHistory)} data-testid="toggle-history-btn">
            <History className="w-3.5 h-3.5" /> {showHistory ? "Hide" : "Show"} verification history ({prop.verification_history.length})
          </button>
          {showHistory && (
            <div className="mt-2 space-y-1 max-h-64 overflow-y-auto border border-slate-100 rounded-md p-2 bg-slate-50" data-testid="verification-history">
              {[...prop.verification_history].reverse().map((h, i) => (
                <div key={i} className="text-[11px] text-slate-700 border-b border-slate-100 last:border-b-0 py-1">
                  <span className="font-mono text-slate-500">{(h.changed_at || "").slice(0,16).replace("T", " ")}</span>
                  <span className="ml-2 font-semibold">{FIELD_LABEL[h.field] || h.field}</span>
                  <span className="ml-1 text-slate-500">→</span>
                  <span className="ml-1 font-mono">{String(h.to_value)}</span>
                  <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-500">{(h.evidence_level || "").replace("_", " ")}</span>
                  <span className="ml-2 text-slate-500">by {h.changed_by || "system"}</span>
                  {h.note && <span className="ml-2 text-slate-400 italic">· {h.note}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

function ConflictRow({ label, value, source, onPick, testid }) {
  return (
    <div className="flex items-center justify-between bg-white border border-rose-200 rounded px-2 py-1.5">
      <div className="text-xs">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
        <div><span className="font-mono font-semibold">{String(value ?? "—")}</span> <span className="text-slate-500">· {source || "no source"}</span></div>
      </div>
      <Button size="sm" variant="outline" onClick={onPick} data-testid={testid}>Use this</Button>
    </div>
  );
}

function coerceValue(field, v) {
  if (v === "" || v == null) return v;
  if (field === "bedrooms" || field === "sqft") return Number(v) || 0;
  if (field === "bathrooms" || field === "acquisition_price") return Number(v) || 0;
  return v;
}
